import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import { Close as CloseIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { format, startOfWeek, differenceInDays } from 'date-fns';
import { lt } from 'date-fns/locale';
import {
  getCoachRecurringRanges,
  getAvailableSlotsInRange,
  getWeekDates,
  generateTimeSlots,
  AVAILABILITY_STATES
} from '../utils/firebaseUtils';

const WEEKDAY_NAMES = ['Pirmadienis', 'Antradienis', 'Trečiadienis', 'Ketvirtadienis', 'Penktadienis', 'Šeštadienis', 'Sekmadienis'];

const MIN_DAYS_LONGER_THAN_MONTH = 31;

const SLOT_STYLES = {
  [AVAILABILITY_STATES.SEB_ARENA]: { bg: '#1565c0', label: 'Seb', light: '#e3f2fd' },
  [AVAILABILITY_STATES.VRM_COURTS]: { bg: '#2e7d32', label: 'VRM', light: '#e8f5e9' },
  default: { bg: '#eceff1', label: '—', light: '#f5f5f5' }
};

const getSlotStyle = (status) => SLOT_STYLES[status] || SLOT_STYLES.default;

const AdminTreneriuNuolatiniaiLaikaiPage = () => {
  const [ranges, setRanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRange, setDialogRange] = useState(null);
  const [dialogSlots, setDialogSlots] = useState({});
  const [dialogLoading, setDialogLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getCoachRecurringRanges()
      .then((list) => {
        if (cancelled) return;
        const withDuration = list.map((r) => {
          const from = new Date(r.validFrom);
          const to = new Date(r.validTo);
          const days = differenceInDays(to, from) + 1;
          return { ...r, days };
        });
        const longerThanMonth = withDuration.filter((r) => r.days > MIN_DAYS_LONGER_THAN_MONTH);
        longerThanMonth.sort((a, b) => {
          const nameCmp = (a.coachName || '').localeCompare(b.coachName || '', 'lt');
          if (nameCmp !== 0) return nameCmp;
          return a.validFrom.localeCompare(b.validFrom);
        });
        setRanges(longerThanMonth);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Klaida kraunant.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleOpenDialog = (range) => {
    setDialogRange(range);
    setDialogOpen(true);
    if (dialogSlots[range.id] === undefined) {
      setDialogLoading(true);
      getAvailableSlotsInRange(range.validFrom, range.validTo, range.coachId)
        .then((slots) => setDialogSlots((prev) => ({ ...prev, [range.id]: slots })))
        .catch((err) => {
          console.error(err);
          setDialogSlots((prev) => ({ ...prev, [range.id]: null }));
        })
        .finally(() => setDialogLoading(false));
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogRange(null);
  };

  const timeSlots = generateTimeSlots();
  const slotsForDialog = dialogRange ? dialogSlots[dialogRange.id] : null;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
        Trenerių nuolatiniai laikai
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Rodomi tik intervalai ilgesni nei mėnuo. Paspauskite „Peržiūrėti“, kad atidarytumėte savaitės šabloną langelyje.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 700 }}>Treneris</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Galioja nuo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Galioja iki</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Veiksmai</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ranges.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    Nėra įrašų ilgesnių nei mėnuo.
                  </TableCell>
                </TableRow>
              )}
              {ranges.map((range) => (
                <TableRow key={range.id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{range.coachName || range.coachId}</TableCell>
                  <TableCell>{format(new Date(range.validFrom), 'yyyy-MM-dd', { locale: lt })}</TableCell>
                  <TableCell>{format(new Date(range.validTo), 'yyyy-MM-dd', { locale: lt })}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleOpenDialog(range)}
                      sx={{ textTransform: 'none' }}
                    >
                      Peržiūrėti
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Typography variant="h6" component="span">
            {dialogRange ? `${dialogRange.coachName || dialogRange.coachId} — ${format(new Date(dialogRange.validFrom), 'yyyy-MM-dd', { locale: lt })} – ${format(new Date(dialogRange.validTo), 'yyyy-MM-dd', { locale: lt })}` : ''}
          </Typography>
          <IconButton size="small" onClick={handleCloseDialog} aria-label="Uždaryti">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
          {dialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : slotsForDialog == null ? (
            <Typography variant="body2" color="text.secondary">Nepavyko užkrauti.</Typography>
          ) : slotsForDialog && Object.keys(slotsForDialog).length > 0 ? (
            <RecurringWeekCalendar slotsByDate={slotsForDialog} validFrom={dialogRange.validFrom} timeSlots={timeSlots} />
          ) : (
            <Typography variant="body2" color="text.secondary">Nėra išsaugotų laikų šiame intervale.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

const WEEKDAY_SHORT = ['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'];

function RecurringWeekCalendar({ slotsByDate, validFrom, timeSlots }) {
  const weekStart = startOfWeek(new Date(validFrom), { weekStartsOn: 1 });
  const weekDates = getWeekDates(format(weekStart, 'yyyy-MM-dd'));
  const cellHeight = 36;

  const getSlotStatus = (dateStr, time) => {
    const day = slotsByDate[dateStr];
    if (!day?.timeSlots) return AVAILABILITY_STATES.UNAVAILABLE;
    const slot = day.timeSlots.find((s) => s.time === time);
    return slot?.availability ?? AVAILABILITY_STATES.UNAVAILABLE;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        maxWidth: 920,
        mx: 'auto',
        bgcolor: 'background.paper'
      }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Savaitės šablonas
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Nuo {format(weekStart, 'yyyy-MM-dd', { locale: lt })}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', minWidth: 0 }}>
        <Box
          sx={{
            width: 64,
            flexShrink: 0,
            borderRight: '2px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50'
          }}
        >
          <Box
            sx={{
              height: 56,
              borderBottom: '2px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'grey.100'
            }}
          >
            <Typography variant="body2" fontWeight={700} color="text.secondary">
              Laikas
            </Typography>
          </Box>
          {timeSlots.map((time, idx) => {
            const isHourStart = time.endsWith(':00');
            const isHourEnd = (idx + 1) % 2 === 0;
            return (
              <Box
                key={time}
                sx={{
                  height: cellHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.8125rem',
                  fontWeight: isHourStart ? 600 : 400,
                  color: 'text.secondary',
                  borderBottom: isHourEnd ? '2px solid' : '1px solid',
                  borderColor: 'divider',
                  bgcolor: isHourStart ? 'grey.100' : 'transparent'
                }}
              >
                {isHourStart ? time : ''}
              </Box>
            );
          })}
        </Box>

        {weekDates.map((dateStr, dayIndex) => (
          <Box
            key={dateStr}
            sx={{
              flex: 1,
              minWidth: 0,
              borderRight: dayIndex < weekDates.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider'
            }}
          >
            <Box
              sx={{
                height: 56,
                borderBottom: '2px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.100',
                px: 0.5
              }}
            >
              <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {WEEKDAY_SHORT[dayIndex]}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 0.25 }}>
                {format(new Date(dateStr), 'MM-dd', { locale: lt })}
              </Typography>
            </Box>
            {timeSlots.map((time, index) => {
              const status = getSlotStatus(dateStr, time);
              const style = getSlotStyle(status);
              const isHourEnd = (index + 1) % 2 === 0;
              const isBookable = status !== AVAILABILITY_STATES.UNAVAILABLE;
              return (
                <Box
                  key={`${dateStr}-${time}`}
                  sx={{
                    height: cellHeight,
                    bgcolor: style.bg,
                    color: isBookable ? 'white' : 'text.secondary',
                    borderBottom: isHourEnd ? '2px solid' : '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 600
                  }}
                >
                  {isBookable ? style.label : '—'}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 3,
          flexWrap: 'wrap',
          px: 2,
          py: 2,
          borderTop: '2px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: '#1565c0', boxShadow: 1 }} />
          <Typography variant="body2" fontWeight={500}>Seb arena</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: '#2e7d32', boxShadow: 1 }} />
          <Typography variant="body2" fontWeight={500}>VRM kortai</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: '#eceff1', border: '1px solid', borderColor: 'divider' }} />
          <Typography variant="body2" fontWeight={500}>Nepasiekiama</Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default AdminTreneriuNuolatiniaiLaikaiPage;
